import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import GeneralLoader from '../../../loaders/GeneralLoader';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';

/**
 * SummaryTopManagementRow
 *
 * Top mgmt. row for the General tab Summary card: renders the loading, empty,
 * and populated-list states. The `canViewExtendedData` guard stays in the
 * parent, so this always renders the `card-row last` wrapper (load-bearing for
 * the TD-2315 desktop column-height cap, which targets
 * `.card:has(.top-management-wrap) > .card-row.last`).
 *
 * The desktop cap makes that wrapper an `overflow-y: auto` scroll box, which
 * would capture the mouse wheel and trap page scrolling over this area. A
 * native wheel listener (TD-2535) forwards the wheel to the page while the box
 * is scrollable, so the page is never trapped; the list overflow stays
 * reachable via the scrollbar.
 *
 * @param {Object} props
 * @param {Array} props.topManagement - Array of top management persons
 * @param {boolean} props.isLoadingDetails - Whether details are loading
 */
const SummaryTopManagementRow = ({ topManagement = [], isLoadingDetails = false }) => {
  const rowRef = useRef(null);

  // On desktop the column-height cap turns this row into an overflow-y:auto
  // scroll box; the wheel would otherwise scroll the list and trap the page.
  // Forward the wheel to the page whenever the box is actually scrollable so
  // page scrolling is never trapped (the overflow stays reachable via the
  // scrollbar). Left untouched when the list fits or on mobile (no cap), where
  // the browser already scrolls the page. Native, non-passive listener because
  // React's onWheel is passive and cannot call preventDefault.
  useEffect(() => {
    const el = rowRef.current;
    if (!el) return undefined;

    const handleWheel = (e) => {
      if (!e.deltaY) return;
      const canScroll = el.scrollHeight > el.clientHeight;
      if (!canScroll) return;
      e.preventDefault();
      const pixels = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
      window.scrollBy({ top: pixels });
    };

    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, []);

  return (
    <div className="card-row last" ref={rowRef}>
      <div className={`property ${dataSourceClass(FIELD_SOURCE.topManagement)}`}>Top mgmt.</div>
      {isLoadingDetails && <GeneralLoader wrapClassName="top-management-loader" />}
      {!isLoadingDetails && !topManagement?.length && (
        <div className="value">{PUNCTUATION.EMPTY_VALUE}</div>
      )}
      {!isLoadingDetails && !!topManagement?.length && (
        <table className="top-management-wrap">
          <tbody>
            {topManagement.map((person, idx) => {
              return (
                <tr
                  key={`${person.first_name}-${person.last_name}-${person.manager_function || ''}-${idx}`}
                >
                  <td className="person-name">
                    {person.first_name} {person.last_name}
                    {person.manager_function && ` (${person.manager_function})`}
                  </td>
                  <td className="person-icon">{/* Icon placeholder */}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

SummaryTopManagementRow.propTypes = {
  topManagement: PropTypes.array,
  isLoadingDetails: PropTypes.bool
};

export default SummaryTopManagementRow;
